import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import FoireannImportModal from '../components/FoireannImportModal'
import {
  slugify, buildBaseSquadSlug, findCollidingSquad, appendLabelToSlug, appendLabelToName, areNamesIndistinguishable,
  findAvailableSlug, notifySquadRelabeled, RETROACTIVE_LABEL, COLLISION_LABEL_PLACEHOLDERS
} from '../utils/squadPublicUrl'

const isAdminRole = (role) => role === 'club_admin' || role === 'squad_admin'

function getSportOptions(gender) {
  if (gender === 'girls') {
    return [
      { value: 'football', label: 'Gaelic Football' },
      { value: 'camogie', label: 'Camogie' },
      { value: 'dual_ladies', label: 'Dual (Football & Camogie)' },
    ]
  }
  return [
    { value: 'football', label: 'Gaelic Football' },
    { value: 'hurling', label: 'Hurling' },
    { value: 'dual', label: 'Dual (Football & Hurling)' },
  ]
}

function getSportLabel(sport) {
  if (sport === 'football') return 'Gaelic Football'
  if (sport === 'hurling') return 'Hurling'
  if (sport === 'camogie') return 'Camogie'
  if (sport === 'dual') return 'Dual (Football & Hurling)'
  if (sport === 'dual_ladies') return 'Dual (Football & Camogie)'
  return sport
}

export default function Squad({ session, profile }) {
  const [squads, setSquads] = useState([])
  const [activeSquadId, setActiveSquadId] = useState(null)
  const [activeView, setActiveView] = useState('players')
  const [players, setPlayers] = useState([])
  const [playerStats, setPlayerStats] = useState({})
  const [showAddSquad, setShowAddSquad] = useState(false)
  const [showAddPlayer, setShowAddPlayer] = useState(false)
  const [playerSearch, setPlayerSearch] = useState('')
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showFoireannImport, setShowFoireannImport] = useState(false)
  const [squadForm, setSquadForm] = useState({ name: '', sport: 'football', age_group: '', gender: 'boys' })
  const [collisionSquad, setCollisionSquad] = useState(null)
  const [publicUrlLabel, setPublicUrlLabel] = useState('')
  const [labelPlaceholder] = useState(() => COLLISION_LABEL_PLACEHOLDERS[Math.floor(Math.random() * COLLISION_LABEL_PLACEHOLDERS.length)])
  const [playerForm, setPlayerForm] = useState({ full_name: '', full_name_irish: '', date_of_birth: '', foireann_id: '', is_guest: false })
  const [editingSquad, setEditingSquad] = useState(null)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [editPlayerForm, setEditPlayerForm] = useState({ full_name: '', full_name_irish: '', date_of_birth: '', foireann_id: '', is_guest: false })
  const [showFoireannFields, setShowFoireannFields] = useState(false)
  const [showEditFoireannFields, setShowEditFoireannFields] = useState(false)
  const [clubAdminName, setClubAdminName] = useState('')

  // Managers (Foireann manager list — independent of app accounts)
  const [managers, setManagers] = useState([])
  const [showAddManager, setShowAddManager] = useState(false)
  const [managerForm, setManagerForm] = useState({ full_name: '', full_name_irish: '', foireann_id: '' })
  const [editingManager, setEditingManager] = useState(null)
  const [editManagerForm, setEditManagerForm] = useState({ full_name: '', full_name_irish: '', foireann_id: '' })

  const isAdmin = isAdminRole(profile?.role)

  useEffect(() => { if (profile) loadSquads() }, [profile])

  async function loadSquads() {
    // Load club admin name
    const { data: adminData } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('club_id', profile.club_id)
      .eq('role', 'club_admin')
      .maybeSingle()
    if (adminData) setClubAdminName(adminData.full_name)
    let query = supabase.from('squads').select('*, squad_teams(team_number, label)').eq('club_id', profile.club_id).order('name')

    // Non-admins only see squads they are members of
    if (profile?.role !== 'club_admin') {
      const { data: memberships } = await supabase
        .from('squad_members')
        .select('squad_id')
        .eq('profile_id', profile.id)
        .eq('approved', true)
      const squadIds = (memberships || []).map(m => m.squad_id)
      if (squadIds.length === 0) { setSquads([]); setActiveSquadId(null); return }
      query = query.in('id', squadIds)
    }

    const { data } = await query
    const newSquads = data || []
    setSquads(newSquads)
    setActiveSquadId(prev => {
      if (prev && newSquads.find(s => s.id === prev)) return prev
      return newSquads.length > 0 ? newSquads[0].id : null
    })
  }

  useEffect(() => {
    setPlayerSearch('')
    if (activeSquadId) { loadPlayers(activeSquadId); loadStats(activeSquadId); loadManagers(activeSquadId) }
    else { setPlayers([]); setPlayerStats({}); setManagers([]) }
  }, [activeSquadId])

  useEffect(() => {
    if (!showAddSquad || !squadForm.age_group) { setCollisionSquad(null); setPublicUrlLabel(''); return }
    const candidate = { current_grade: squadForm.age_group, gender: squadForm.gender, sport: squadForm.sport }
    const match = findCollidingSquad(squads, candidate)
    setCollisionSquad(match)
    if (!match) setPublicUrlLabel('')
  }, [showAddSquad, squadForm.age_group, squadForm.gender, squadForm.sport, squads])

  async function loadPlayers(squadId) {
    const { data } = await supabase.from('players')
      .select('*').eq('squad_id', squadId).eq('is_active', true).order('jersey_number')
    setPlayers(data || [])
  }

  async function loadManagers(squadId) {
    const { data } = await supabase.from('squad_managers')
      .select('*').eq('squad_id', squadId).order('full_name')
    setManagers(data || [])
  }

  async function loadStats(squadId) {
    const { data: fixtures } = await supabase.from('fixtures')
      .select('id').eq('squad_id', squadId).eq('status', 'completed')
    if (!fixtures || fixtures.length === 0) { setPlayerStats({}); return }
    const fixtureIds = fixtures.map(f => f.id)
    const { data: lineups } = await supabase.from('lineups')
      .select('*, players(id, full_name, jersey_number)')
      .in('fixture_id', fixtureIds)
    const { data: events } = await supabase.from('match_events')
      .select('*').in('fixture_id', fixtureIds)
    if (!lineups || !events) return
    const stats = {}
    function initPlayer(playerId, playerName, jerseyNumber) {
      if (!stats[playerId]) {
        stats[playerId] = {
          name: playerName, jersey_number: jerseyNumber,
          gamesPlayed: 0, gamesStarted: 0, gamesCameOn: 0,
          goals: 0, points: 0, twoPointers: 0,
          yellowCards: 0, redCards: 0, blackCards: 0,
          kickoutsWon: 0, freesWon: 0,
        }
      }
    }
    const fixturePlayerMap = {}
    lineups.forEach(l => {
      if (!l.players) return
      const pid = l.players.id
      initPlayer(pid, l.players.full_name, l.players.jersey_number)
      const key = l.fixture_id + '-' + pid
      if (!fixturePlayerMap[key]) {
        fixturePlayerMap[key] = { starting: l.starting, subbedOn: false }
        if (l.starting) { stats[pid].gamesStarted++; stats[pid].gamesPlayed++ }
      }
    })
    events.forEach(e => {
      if (!e.player_id) return
      const pid = e.player_id
      if (e.event_type === 'sub_on') {
        const lineupEntry = lineups.find(l => l.fixture_id === e.fixture_id && l.players?.id === pid)
        if (!lineupEntry || !lineupEntry.starting) {
          if (stats[pid]) {
            const key = e.fixture_id + '-' + pid
            if (!fixturePlayerMap[key]?.subbedOn) {
              stats[pid].gamesPlayed++; stats[pid].gamesCameOn++
              if (fixturePlayerMap[key]) fixturePlayerMap[key].subbedOn = true
              else fixturePlayerMap[key] = { starting: false, subbedOn: true }
            }
          }
        }
      }
      if (!stats[pid]) return
      if (e.event_type === 'goal') stats[pid].goals++
      if (e.event_type === 'point') stats[pid].points++
      if (e.event_type === 'twopoint') stats[pid].twoPointers++
      if (e.event_type === 'yellow_card') stats[pid].yellowCards++
      if (e.event_type === 'red_card') stats[pid].redCards++
      if (e.event_type === 'black_card') stats[pid].blackCards++
      if (e.event_type === 'kickout_won') stats[pid].kickoutsWon++
      if (e.event_type === 'free_won') stats[pid].freesWon++
    })
    setPlayerStats(stats)
  }

  async function addSquad() {
    if (!squadForm.name) return
    if (collisionSquad && !publicUrlLabel.trim()) return

    const clubSlug = profile.clubs?.slug
    const seasonYear = new Date().getFullYear()
    const baseSlug = buildBaseSquadSlug({ clubSlug, gradeText: squadForm.age_group, gender: squadForm.gender, sport: squadForm.sport, seasonYear })
    const birthYearMatch = /^U(\d+)$/i.exec((squadForm.age_group || '').trim())
    const birthYear = birthYearMatch && !['20', '21'].includes(birthYearMatch[1]) ? seasonYear - Number(birthYearMatch[1]) : null

    let name = squadForm.name
    let publicUrlLabelValue = null
    let finalSlug = baseSlug
    if (collisionSquad) {
      publicUrlLabelValue = publicUrlLabel.trim()
      name = appendLabelToName(name, publicUrlLabelValue)
      finalSlug = appendLabelToSlug(baseSlug, publicUrlLabelValue)
    }
    finalSlug = await findAvailableSlug(supabase, finalSlug)

    const { data, error } = await supabase.from('squads')
      .insert({
        ...squadForm, name, club_id: profile.club_id,
        current_grade: squadForm.age_group || null,
        birth_year: birthYear, public_url_label: publicUrlLabelValue, slug: finalSlug
      }).select().single()

    if (error) {
      alert('Failed to create squad: ' + error.message)
      return
    }

    // Non-club-admins only see squads they're a member of, so the creator
    // must be added as a squad_admin member or their new squad stays invisible to them.
    if (profile?.role !== 'club_admin' && data) {
      const { error: memberError } = await supabase.from('squad_members').insert({
        squad_id: data.id, profile_id: profile.id, role: 'squad_admin', approved: true
      })
      if (memberError) {
        alert('Squad created, but failed to add you to it: ' + memberError.message + '\n\nAsk your club admin to add you via the Admin tab.')
      }
    }

    // Retroactively give the colliding squad a distinguishing label/URL too,
    // but only if its name doesn't already read as distinct on its own.
    if (collisionSquad && !collisionSquad.public_url_label && areNamesIndistinguishable(squadForm.name, collisionSquad.name)) {
      const existingBaseSlug = buildBaseSquadSlug({
        clubSlug,
        gradeText: collisionSquad.current_grade || collisionSquad.age_group,
        gender: collisionSquad.gender, sport: collisionSquad.sport, seasonYear
      })
      const existingName = appendLabelToName(collisionSquad.name, RETROACTIVE_LABEL)
      const existingSlug = await findAvailableSlug(supabase, appendLabelToSlug(existingBaseSlug, RETROACTIVE_LABEL))
      await supabase.from('squads').update({
        name: existingName, slug: existingSlug, public_url_label: RETROACTIVE_LABEL
      }).eq('id', collisionSquad.id)
      await notifySquadRelabeled(supabase, {
        squad: collisionSquad, newName: existingName, newSlug: existingSlug, clubName: profile.clubs?.club_name || profile.clubs?.name || ''
      })
    }

    setSquadForm({ name: '', sport: 'football', age_group: '', gender: 'boys' })
    setShowAddSquad(false)
    await loadSquads()
    if (data) setActiveSquadId(data.id)
  }

  async function saveSquadName() {
    if (!editingSquad || !editingSquad.name.trim()) return
    const original = squads.find(s => s.id === editingSquad.id)
    let slug = (editingSquad.slug || '').trim()
    if (slug !== (original?.slug || '')) {
      slug = await findAvailableSlug(supabase, slugify(slug))
    }
    await supabase.from('squads').update({
      name: editingSquad.name.trim(),
      sport: editingSquad.sport,
      gender: editingSquad.gender,
      current_grade: editingSquad.current_grade || null,
      age_group: editingSquad.age_group || editingSquad.current_grade || null,
      slug: slug || null,
    }).eq('id', editingSquad.id)

    // Save squad_teams changes
    const teams = editingSquad.squad_teams || []
    for (const team of teams) {
      if (team.isNew) {
        await supabase.from('squad_teams').insert({
          squad_id: editingSquad.id,
          team_number: team.team_number,
          label: team.label.trim() || 'Team ' + team.team_number
        })
      } else {
        await supabase.from('squad_teams').update({ label: team.label.trim() || 'Team ' + team.team_number })
          .eq('squad_id', editingSquad.id).eq('team_number', team.team_number)
      }
    }
    // Delete removed teams
    for (const teamNum of (editingSquad._teamsToDelete || [])) {
      await supabase.from('squad_teams').delete()
        .eq('squad_id', editingSquad.id).eq('team_number', teamNum)
    }
    setEditingSquad(null)
    loadSquads()
  }

  async function deleteSquad(squadId) {
    if (!window.confirm('Delete this squad? This will also remove all players in the squad.')) return
    await supabase.from('players').update({ is_active: false }).eq('squad_id', squadId)
    await supabase.from('squads').delete().eq('id', squadId)
    setSquads(prev => {
      const remaining = prev.filter(s => s.id !== squadId)
      setActiveSquadId(remaining.length > 0 ? remaining[0].id : null)
      return remaining
    })
    setPlayers([])
  }

  async function addPlayer() {
    if (!playerForm.full_name || !activeSquadId) return
    await supabase.from('players').insert({
      full_name: playerForm.full_name,
      jersey_number: null,
      position: null,
      full_name_irish: playerForm.full_name_irish || null,
      date_of_birth: playerForm.date_of_birth || null,
      foireann_id: playerForm.foireann_id || null,
      is_guest: playerForm.is_guest || false,
      squad_id: activeSquadId, club_id: profile.club_id
    })
    setPlayerForm({ full_name: '', full_name_irish: '', date_of_birth: '', foireann_id: '', is_guest: false })
    setShowAddPlayer(false)
    setShowFoireannFields(false)
    loadPlayers(activeSquadId)
  }

  async function bulkAddPlayers() {
    if (!bulkText.trim() || !activeSquadId) return
    setBulkLoading(true)
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l.length > 0)
    const toInsert = lines.map(line => {
      const full_name = line.split(',')[0].trim()
      return { full_name, squad_id: activeSquadId, club_id: profile.club_id }
    }).filter(p => p.full_name)
    if (toInsert.length === 0) { setBulkLoading(false); return }
    await supabase.from('players').insert(toInsert)
    setBulkText('')
    setShowBulkAdd(false)
    setBulkLoading(false)
    loadPlayers(activeSquadId)
  }

  async function savePlayer() {
    if (!editingPlayer) return
    await supabase.from('players').update({
      full_name: editPlayerForm.full_name,
      full_name_irish: editPlayerForm.full_name_irish || null,
      date_of_birth: editPlayerForm.date_of_birth || null,
      foireann_id: editPlayerForm.foireann_id || null,
      is_guest: editPlayerForm.is_guest || false
    }).eq('id', editingPlayer.id)
    setEditingPlayer(null)
    setShowEditFoireannFields(false)
    loadPlayers(activeSquadId)
  }

  async function deletePlayer(playerId) {
    if (!window.confirm('Remove this player from the squad?')) return
    await supabase.from('players').update({ is_active: false }).eq('id', playerId)
    loadPlayers(activeSquadId)
  }

  function startEditPlayer(player) {
    setEditingPlayer(player)
    setEditPlayerForm({
      full_name: player.full_name,
      full_name_irish: player.full_name_irish || '',
      date_of_birth: player.date_of_birth || '',
      foireann_id: player.foireann_id || '',
      is_guest: player.is_guest || false
    })
    setShowEditFoireannFields(!!(player.full_name_irish || player.date_of_birth || player.foireann_id))
  }

  // ── Manager (Foireann list) functions ──────────────────────────────────────

  async function addManager() {
    if (!managerForm.full_name || !activeSquadId) return
    await supabase.from('squad_managers').insert({
      full_name: managerForm.full_name,
      full_name_irish: managerForm.full_name_irish || null,
      foireann_id: managerForm.foireann_id || null,
      squad_id: activeSquadId
    })
    setManagerForm({ full_name: '', full_name_irish: '', foireann_id: '' })
    setShowAddManager(false)
    loadManagers(activeSquadId)
  }

  async function saveManager() {
    if (!editingManager) return
    await supabase.from('squad_managers').update({
      full_name: editManagerForm.full_name,
      full_name_irish: editManagerForm.full_name_irish || null,
      foireann_id: editManagerForm.foireann_id || null
    }).eq('id', editingManager.id)
    setEditingManager(null)
    loadManagers(activeSquadId)
  }

  async function deleteManager(managerId) {
    if (!window.confirm('Remove this manager from the list?')) return
    await supabase.from('squad_managers').delete().eq('id', managerId)
    loadManagers(activeSquadId)
  }

  function startEditManager(manager) {
    setEditingManager(manager)
    setEditManagerForm({
      full_name: manager.full_name,
      full_name_irish: manager.full_name_irish || '',
      foireann_id: manager.foireann_id || ''
    })
  }

  const activeSquad = squads.find(s => s.id === activeSquadId)
  const ageGroups = ['Senior','U20','Minor','U16','U15','U14','U13','U12','U11','U10']
  const statsList = Object.entries(playerStats)
    .map(([id, s]) => ({ id, ...s, totalPts: s.goals * 3 + s.points + s.twoPointers * 2 }))
    .sort((a, b) => b.totalPts - a.totalPts)
  const hasStats = statsList.length > 0

  return (
    <div>
      <div style={header}>
        <h2 style={h2}>Squad</h2>
        {isAdmin && (
          <button style={addBtn} onClick={() => setShowAddSquad(!showAddSquad)}>+ Squad</button>
        )}
      </div>

      {/* Add squad form — admins only */}
      {showAddSquad && isAdmin && (
        <div style={formBox}>
          <p style={formTitle}>New squad</p>
          <input style={input} placeholder="Squad name *" value={squadForm.name}
            onChange={e => setSquadForm({ ...squadForm, name: e.target.value })} />

          {/* Gender first — determines sport options */}
          <select style={input} value={squadForm.gender}
            onChange={e => {
              const newGender = e.target.value
              const defaultSport = newGender === 'girls' ? 'football' : 'football'
              setSquadForm({ ...squadForm, gender: newGender, sport: defaultSport })
            }}>
            <option value="boys">Boys</option>
            <option value="girls">Girls</option>
          </select>

          {/* Sport options based on gender */}
          <select style={input} value={squadForm.sport}
            onChange={e => setSquadForm({ ...squadForm, sport: e.target.value })}>
            {getSportOptions(squadForm.gender).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select style={input} value={squadForm.age_group}
            onChange={e => setSquadForm({ ...squadForm, age_group: e.target.value })}>
            <option value="">Age group (optional)</option>
            {ageGroups.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          {collisionSquad && (
            <div style={{ background: '#FFF6E5', border: '0.5px solid #C98A1B', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', color: '#7A5B10', lineHeight: '1.5', marginBottom: '10px' }}>
                Your club already has a {squadForm.age_group} {squadForm.gender === 'boys' ? 'Boys' : 'Girls'} squad — <strong>{collisionSquad.name}</strong>. What's different about this one?
              </p>
              <input style={{ ...input, marginBottom: 0 }} type="text" placeholder={labelPlaceholder}
                value={publicUrlLabel} onChange={e => setPublicUrlLabel(e.target.value)} />
            </div>
          )}

          <button style={saveBtn} onClick={addSquad} disabled={collisionSquad && !publicUrlLabel.trim()}>Create Squad</button>
        </div>
      )}

      {/* Squad tabs */}
      {squads.length > 0 && activeSquad?.slug && (
        <div style={{ padding: '8px 16px 0' }}>
          <span
            onClick={() => window.open('https://gaamatchtracker.ie/public/' + activeSquad.slug, '_blank')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#E6F1FB', color: '#185FA5', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            🔗 Share Live Score Page
          </span>
        </div>
      )}
      {squads.length > 0 && (
        <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '0.5px solid #e0e0e0', padding: '0 16px' }}>
          {squads.map(s => (
            <button key={s.id} onClick={() => setActiveSquadId(s.id)}
              style={{ ...tabBtn, color: activeSquadId === s.id ? '#185FA5' : '#888', borderBottom: activeSquadId === s.id ? '2px solid #185FA5' : '2px solid transparent' }}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* No squads message for coaches */}
      {squads.length === 0 && !showAddSquad && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
          <p style={{ fontSize: '32px', marginBottom: '12px' }}>👥</p>
          {isAdmin
            ? <p>No squads yet. Tap + Squad to create one.</p>
: <p>You haven't been assigned to a squad yet. Contact your club admin{clubAdminName ? ' (' + clubAdminName + ')' : ''}.</p>
          }
        </div>
      )}

      {activeSquad && (
        <>
          {/* Squad info + actions */}
          <div style={{ padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0', background: '#f8f8f8' }}>
            {editingSquad?.id === activeSquad.id ? (
              <div>
                <p style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Edit squad details</p>
                <input style={input} placeholder="Squad name *" value={editingSquad.name}
                  onChange={e => setEditingSquad({ ...editingSquad, name: e.target.value })} autoFocus />
                <div style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: '11px', color: '#888', marginBottom: 6 }}>TEAMS ({(editingSquad.squad_teams || []).length}/5)</p>
                  {(editingSquad.squad_teams || []).sort((a, b) => a.team_number - b.team_number).map((team, i) => (
                    <div key={team.team_number} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#888', minWidth: 20 }}>#{team.team_number}</span>
                      <input
                        style={{ ...input, flex: 1, marginBottom: 0 }}
                        value={team.label}
                        onChange={e => {
                          const updated = (editingSquad.squad_teams || []).map(t =>
                            t.team_number === team.team_number ? { ...t, label: e.target.value } : t
                          )
                          setEditingSquad({ ...editingSquad, squad_teams: updated })
                        }}
                      />
                      {team.team_number > 1 && (
                        <button
                          onClick={async () => {
                            const { count } = await supabase
                              .from('fixtures')
                              .select('*', { count: 'exact', head: true })
                              .eq('squad_id', editingSquad.id)
                              .eq('team_number', team.team_number)
                            if (count > 0) {
                              alert('Cannot remove — this team has ' + count + ' fixture' + (count !== 1 ? 's' : '') + '. You can rename it instead.')
                              return
                            }
                            setEditingSquad({
                              ...editingSquad,
                              squad_teams: (editingSquad.squad_teams || []).filter(t => t.team_number !== team.team_number),
                              _teamsToDelete: [...(editingSquad._teamsToDelete || []), team.team_number]
                            })
                          }}
                          style={{ padding: '4px 8px', background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#c0392b', flexShrink: 0 }}
                        >✕</button>
                      )}
                    </div>
                  ))}
                  {(editingSquad.squad_teams || []).length < 5 && (
                    <button
                      onClick={() => {
                        const existing = editingSquad.squad_teams || []
                        const nextNum = Math.max(...existing.map(t => t.team_number), 0) + 1
                        setEditingSquad({
                          ...editingSquad,
                          squad_teams: [...existing, { team_number: nextNum, label: 'Team ' + nextNum, isNew: true }]
                        })
                      }}
                      style={{ fontSize: 12, padding: '5px 10px', background: '#E6F1FB', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 6, cursor: 'pointer' }}
                    >+ Add Team</button>
                  )}
                </div>
                <select style={input} value={editingSquad.gender || 'boys'}
                  onChange={e => setEditingSquad({ ...editingSquad, gender: e.target.value })}>
                  <option value="boys">Boys</option>
                  <option value="girls">Ladies</option>
                </select>
                <select style={input} value={editingSquad.sport || 'football'}
                  onChange={e => setEditingSquad({ ...editingSquad, sport: e.target.value })}>
                  {(editingSquad.gender === 'girls') ? (
                    <>
                      <option value="football">Gaelic Football</option>
                      <option value="camogie">Camogie</option>
                      <option value="dual_ladies">Dual (Football & Camogie)</option>
                    </>
                  ) : (
                    <>
                      <option value="football">Gaelic Football</option>
                      <option value="hurling">Hurling</option>
                      <option value="dual">Dual (Football & Hurling)</option>
                    </>
                  )}
                </select>
                <select style={input} value={editingSquad.current_grade || editingSquad.age_group || ''}
                  onChange={e => setEditingSquad({ ...editingSquad, current_grade: e.target.value, age_group: e.target.value })}>
                  <option value="">No age grade</option>
                  {['U12','U13','U14','U15','U16','U17','U18','Minor','U20','Senior'].map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: '#888', marginBottom: 4 }}>
                  PUBLIC URL — changing this every season isn't needed, the age grade above can be updated freely without affecting it
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#888' }}>gaamatchtracker.ie/public/</span>
                  <input style={{ ...input, flex: 1, marginBottom: 0 }} value={editingSquad.slug || ''}
                    onChange={e => setEditingSquad({ ...editingSquad, slug: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button style={{ ...saveBtn, flex: 1, marginBottom: 0 }} onClick={saveSquadName}>Save</button>
                  <button onClick={() => setEditingSquad(null)}
                    style={{ flex: 1, padding: '12px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '13px', fontWeight: '500' }}>{activeSquad.name}</p>
                  <p style={{ fontSize: '11px', color: '#888' }}>
                    {getSportLabel(activeSquad.sport)}
                    {activeSquad.gender ? ' · ' + (activeSquad.gender === 'boys' ? 'Boys' : 'Ladies') : ''}
                    {activeSquad.current_grade || activeSquad.age_group ? ' · ' + (activeSquad.current_grade || activeSquad.age_group) : ''}
                    {' · ' + players.length + ' players'}
                  </p>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => setEditingSquad({ ...activeSquad })}
                      style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                      ✏️ Edit
                    </button>
                    <button onClick={() => deleteSquad(activeSquad.id)}
                      style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }}>
                      🗑 Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Players / Managers / Stats toggle */}
          <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0' }}>
            {['players', 'managers', 'stats'].map(view => (
              <button key={view} onClick={() => setActiveView(view)}
                style={{ flex: 1, padding: '10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: activeView === view ? '#185FA5' : '#888', borderBottom: activeView === view ? '2px solid #185FA5' : '2px solid transparent' }}>
                {view === 'players' ? '👥 Players (' + players.length + ')' : view === 'managers' ? '🧢 Managers (' + managers.length + ')' : '📊 Stats'}
              </button>
            ))}
          </div>

          {/* PLAYERS VIEW */}
          {activeView === 'players' && (
            <>
              {isAdmin && (
                <div style={{ padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={addBtn} onClick={() => { setShowAddPlayer(!showAddPlayer); setShowBulkAdd(false) }}>
                      {showAddPlayer ? 'Cancel' : '+ Add Player'}
                    </button>
                    <button style={{ ...addBtn, background: '#E6F1FB', color: '#185FA5', border: '1px solid #185FA5' }} onClick={() => { setShowBulkAdd(!showBulkAdd); setShowAddPlayer(false) }}>
                      {showBulkAdd ? 'Cancel' : '+ Bulk Add'}
                    </button>
                    <button style={{ ...addBtn, background: '#fff8e1', color: '#b7860b', border: '1px solid #f0c040' }} onClick={() => setShowFoireannImport(true)}>
                      📄 Foireann Import
                    </button>
                  </div>
                </div>
              )}

              {showFoireannImport && (
                <FoireannImportModal
                  squadId={activeSquadId}
                  onClose={() => setShowFoireannImport(false)}
                  onImported={async () => { await loadSquads(); await loadPlayers(activeSquadId); await loadManagers(activeSquadId); setShowFoireannImport(false) }}
                />
              )}

              {showBulkAdd && isAdmin && (
                <div style={{ ...formBox, margin: '0 16px 12px' }}>
                  <p style={formTitle}>Bulk add players to {activeSquad?.name}</p>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                    One player per line — just the name.{'\n'}
                    Foireann details (Irish name, DOB, ID) can be added individually via Edit after importing.
                  </p>
                  <textarea
                    style={{ ...input, height: '160px', resize: 'vertical', fontFamily: 'sans-serif', lineHeight: '1.6' }}
                    placeholder={'Seán Murphy\nPaddy Ryan\nConor Walsh'}
                    value={bulkText}
                    onChange={e => setBulkText(e.target.value)}
                  />
                  <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
                    {bulkText.split('\n').filter(l => l.trim()).length} players to add
                  </p>
                  <button style={saveBtn} onClick={bulkAddPlayers} disabled={bulkLoading}>
                    {bulkLoading ? 'Adding...' : 'Add All Players'}
                  </button>
                </div>
              )}
              {showAddPlayer && isAdmin && (
                <div style={{ ...formBox, margin: '0 16px 12px' }}>
                  <p style={formTitle}>Add player to {activeSquad.name}</p>
                  <input style={input} placeholder="Full name *" value={playerForm.full_name}
                    onChange={e => setPlayerForm({ ...playerForm, full_name: e.target.value })} />

                  <label style={guestCheckboxLabel}>
                    <input type="checkbox" checked={playerForm.is_guest}
                      onChange={e => setPlayerForm({ ...playerForm, is_guest: e.target.checked })} />
                    <span>Visiting / guest player <span style={{ color: '#888', fontWeight: '400' }}>(not a registered squad member — e.g. playing up from a younger age group)</span></span>
                  </label>

                  <button type="button" onClick={() => setShowFoireannFields(!showFoireannFields)}
                    style={foireannToggleBtn}>
                    {showFoireannFields ? '▾' : '▸'} Foireann details (optional)
                  </button>
                  {showFoireannFields && (
                    <div style={{ marginTop: '8px' }}>
                      <input style={input} placeholder="Name in Irish (optional)" value={playerForm.full_name_irish}
                        onChange={e => setPlayerForm({ ...playerForm, full_name_irish: e.target.value })} />
                      <input style={input} type="date" placeholder="Date of birth (optional)" value={playerForm.date_of_birth}
                        onChange={e => setPlayerForm({ ...playerForm, date_of_birth: e.target.value })} />
                      <input style={input} placeholder="Foireann ID (optional)" value={playerForm.foireann_id}
                        onChange={e => setPlayerForm({ ...playerForm, foireann_id: e.target.value })} />
                    </div>
                  )}

                  <button style={saveBtn} onClick={addPlayer}>Add Player</button>
                </div>
              )}

              {players.length > 5 && (
                <div style={{ padding: '8px 16px' }}>
                  <input
                    placeholder="🔍 Search players"
                    value={playerSearch}
                    onChange={e => setPlayerSearch(e.target.value)}
                    style={{ display: 'block', width: '100%', padding: '8px 12px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '13px', background: '#f8f8f8', boxSizing: 'border-box' }}
                  />
                </div>
              )}
              {players.length === 0 && (
                <div style={{ padding: '32px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                  {isAdmin ? 'No players yet. Tap + Add Player to add some.' : 'No players added yet.'}
                </div>
              )}

            {players.filter(p => !playerSearch.trim() || p.full_name.toLowerCase().includes(playerSearch.toLowerCase())).map(p => (
                <div key={p.id}>
                  {editingPlayer?.id === p.id ? (
                    <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0e0', background: '#f8f8f8' }}>
                      <p style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Editing {p.full_name}</p>
                      <input style={input} placeholder="Full name *" value={editPlayerForm.full_name}
                        onChange={e => setEditPlayerForm({ ...editPlayerForm, full_name: e.target.value })} />

                      <label style={guestCheckboxLabel}>
                        <input type="checkbox" checked={editPlayerForm.is_guest}
                          onChange={e => setEditPlayerForm({ ...editPlayerForm, is_guest: e.target.checked })} />
                        <span>Visiting / guest player <span style={{ color: '#888', fontWeight: '400' }}>(not a registered squad member)</span></span>
                      </label>

                      <button type="button" onClick={() => setShowEditFoireannFields(!showEditFoireannFields)}
                        style={foireannToggleBtn}>
                        {showEditFoireannFields ? '▾' : '▸'} Foireann details (optional)
                      </button>
                      {showEditFoireannFields && (
                        <div style={{ marginTop: '8px' }}>
                          <input style={input} placeholder="Name in Irish (optional)" value={editPlayerForm.full_name_irish}
                            onChange={e => setEditPlayerForm({ ...editPlayerForm, full_name_irish: e.target.value })} />
                          <input style={input} type="date" placeholder="Date of birth (optional)" value={editPlayerForm.date_of_birth}
                            onChange={e => setEditPlayerForm({ ...editPlayerForm, date_of_birth: e.target.value })} />
                          <input style={input} placeholder="Foireann ID (optional)" value={editPlayerForm.foireann_id}
                            onChange={e => setEditPlayerForm({ ...editPlayerForm, foireann_id: e.target.value })} />
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <button style={{ ...saveBtn, flex: 1, marginBottom: 0 }} onClick={savePlayer}>Save Changes</button>
                        <button onClick={() => { setEditingPlayer(null); setShowEditFoireannFields(false) }}
                          style={{ flex: 1, padding: '12px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ ...playerRow, background: p.is_guest ? '#FFF9E6' : 'transparent' }}>
                      <div style={{ ...avatar, background: p.is_guest ? '#FAEEDA' : '#E6F1FB', color: p.is_guest ? '#854F0B' : '#185FA5' }}>{p.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '14px' }}>
                          {p.full_name}
                          {p.is_guest && (
                            <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '10px', background: '#FAEEDA', color: '#854F0B', fontWeight: '500', marginLeft: '8px' }}>
                              GUEST
                            </span>
                          )}
                        </p>
                        {(p.full_name_irish || p.foireann_id) && (
                          <span style={{ fontSize: '11px', color: '#888' }}>
                            {p.full_name_irish ? p.full_name_irish : ''}{p.full_name_irish && p.foireann_id ? ' · ' : ''}{p.foireann_id ? 'ID ' + p.foireann_id : ''}
                          </span>
                        )}
                      </div>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => startEditPlayer(p)}
                            style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                            Edit
                          </button>
                          <button onClick={() => deletePlayer(p.id)}
                            style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {isAdmin && (
                <button style={addPlayerBtn} onClick={() => setShowAddPlayer(!showAddPlayer)}>
                  + Add Player
                </button>
              )}
            </>
          )}

          {/* MANAGERS VIEW — Foireann manager list, independent of app accounts */}
          {activeView === 'managers' && (
            <>
              <div style={{ padding: '12px 16px', background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' }}>
                <p style={{ fontSize: '12px', color: '#888', lineHeight: '1.5' }}>
                  This list is for Foireann team sheets — it's separate from app access. Add anyone registered as a manager for this squad, whether or not they use the tracker.
                </p>
              </div>

              {isAdmin && (
                <div style={{ padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <button style={addBtn} onClick={() => setShowAddManager(!showAddManager)}>
                    {showAddManager ? 'Cancel' : '+ Add Manager'}
                  </button>
                </div>
              )}

              {showAddManager && isAdmin && (
                <div style={{ ...formBox, margin: '0 16px 12px' }}>
                  <p style={formTitle}>Add manager to {activeSquad.name}</p>
                  <input style={input} placeholder="Full name *" value={managerForm.full_name}
                    onChange={e => setManagerForm({ ...managerForm, full_name: e.target.value })} />
                  <input style={input} placeholder="Name in Irish (optional)" value={managerForm.full_name_irish}
                    onChange={e => setManagerForm({ ...managerForm, full_name_irish: e.target.value })} />
                  <input style={input} placeholder="Foireann ID (optional)" value={managerForm.foireann_id}
                    onChange={e => setManagerForm({ ...managerForm, foireann_id: e.target.value })} />
                  <button style={saveBtn} onClick={addManager}>Add Manager</button>
                </div>
              )}

              {managers.length === 0 && !showAddManager && (
                <div style={{ padding: '32px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                  {isAdmin ? 'No managers added yet. Tap + Add Manager to add some.' : 'No managers added yet.'}
                </div>
              )}

              {managers.map(m => (
                <div key={m.id}>
                  {editingManager?.id === m.id ? (
                    <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0e0', background: '#f8f8f8' }}>
                      <p style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Editing {m.full_name}</p>
                      <input style={input} placeholder="Full name *" value={editManagerForm.full_name}
                        onChange={e => setEditManagerForm({ ...editManagerForm, full_name: e.target.value })} />
                      <input style={input} placeholder="Name in Irish (optional)" value={editManagerForm.full_name_irish}
                        onChange={e => setEditManagerForm({ ...editManagerForm, full_name_irish: e.target.value })} />
                      <input style={input} placeholder="Foireann ID (optional)" value={editManagerForm.foireann_id}
                        onChange={e => setEditManagerForm({ ...editManagerForm, foireann_id: e.target.value })} />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={{ ...saveBtn, flex: 1 }} onClick={saveManager}>Save Changes</button>
                        <button onClick={() => setEditingManager(null)}
                          style={{ flex: 1, padding: '12px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={playerRow}>
                      <div style={avatar}>{m.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '14px' }}>{m.full_name}</p>
                        <span style={{ fontSize: '11px', color: '#888' }}>
                          {m.full_name_irish ? m.full_name_irish + ' · ' : ''}{m.foireann_id ? 'ID ' + m.foireann_id : 'No Foireann ID'}
                        </span>
                      </div>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => startEditManager(m)}
                            style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                            Edit
                          </button>
                          <button onClick={() => deleteManager(m.id)}
                            style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* STATS VIEW */}
          {activeView === 'stats' && (
            <div>
              {!hasStats ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                  <p style={{ fontSize: '32px', marginBottom: '12px' }}>📊</p>
                  <p style={{ fontWeight: '500', marginBottom: '6px' }}>No stats yet</p>
                  <p style={{ fontSize: '12px' }}>Stats appear here after matches are completed</p>
                </div>
              ) : (
                <>
                  <div style={{ padding: '8px 16px 4px', fontSize: '10px', color: '#888', letterSpacing: '0.3px', fontWeight: '500' }}>SCORING</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' }}>
                          <th style={th}>Player</th>
                          <th style={th}>GP</th>
                          <th style={th}>G</th>
                          <th style={th}>P</th>
                          <th style={th}>2pt</th>
                          <th style={th}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsList.map((s, i) => (
                          <tr key={s.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '500', color: '#185FA5', flexShrink: 0 }}>
                                  {s.jersey_number || '—'}
                                </div>
                                <span style={{ fontSize: '12px' }}>{s.name}</span>
                              </div>
                            </td>
                            <td style={{ ...td, textAlign: 'center', color: '#888' }}>{s.gamesPlayed}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#185FA5', fontWeight: '500' }}>{s.goals}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#3B6D11', fontWeight: '500' }}>{s.points}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#0F6E56', fontWeight: '500' }}>{s.twoPointers}</td>
                            <td style={{ ...td, textAlign: 'center', fontWeight: '600', color: '#333' }}>
                              {s.goals}-{String(s.points + s.twoPointers * 2).padStart(2, '0')}
                              <span style={{ fontSize: '10px', color: '#888', marginLeft: '3px' }}>({s.totalPts})</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ padding: '12px 16px 4px', fontSize: '10px', color: '#888', letterSpacing: '0.3px', fontWeight: '500' }}>APPEARANCES</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' }}>
                          <th style={th}>Player</th>
                          <th style={th}>GP</th>
                          <th style={th}>Started</th>
                          <th style={th}>Sub On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...statsList].sort((a, b) => b.gamesPlayed - a.gamesPlayed).map((s, i) => (
                          <tr key={s.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '500', color: '#185FA5', flexShrink: 0 }}>
                                  {s.jersey_number || '—'}
                                </div>
                                <span>{s.name}</span>
                              </div>
                            </td>
                            <td style={{ ...td, textAlign: 'center', fontWeight: '500' }}>{s.gamesPlayed}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#185FA5' }}>{s.gamesStarted}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#533AB7' }}>{s.gamesCameOn}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ padding: '12px 16px 4px', fontSize: '10px', color: '#888', letterSpacing: '0.3px', fontWeight: '500' }}>DISCIPLINE</div>
                  <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' }}>
                          <th style={th}>Player</th>
                          <th style={th}>🟨</th>
                          <th style={th}>⬛</th>
                          <th style={th}>🟥</th>
                          <th style={th}>KO Won</th>
                          <th style={th}>Free Won</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...statsList]
                          .filter(s => s.yellowCards + s.blackCards + s.redCards + s.kickoutsWon + s.freesWon > 0)
                          .sort((a, b) => (b.yellowCards + b.blackCards + b.redCards) - (a.yellowCards + a.blackCards + a.redCards))
                          .map((s, i) => (
                            <tr key={s.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                              <td style={td}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '500', color: '#185FA5', flexShrink: 0 }}>
                                    {s.jersey_number || '—'}
                                  </div>
                                  <span>{s.name}</span>
                                </div>
                              </td>
                              <td style={{ ...td, textAlign: 'center', color: '#854F0B', fontWeight: '500' }}>{s.yellowCards || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#333', fontWeight: '500' }}>{s.blackCards || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#A32D2D', fontWeight: '500' }}>{s.redCards || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#3B6D11' }}>{s.kickoutsWon || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#0F6E56' }}>{s.freesWon || '—'}</td>
                            </tr>
                          ))}
                        {statsList.filter(s => s.yellowCards + s.blackCards + s.redCards + s.kickoutsWon + s.freesWon > 0).length === 0 && (
                          <tr>
                            <td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>No discipline events recorded</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const header = { padding: '16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center' }
const h2 = { fontSize: '17px', fontWeight: '500', flex: 1 }
const addBtn = { padding: '6px 14px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }
const formBox = { margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }
const formTitle = { fontSize: '13px', color: '#888', marginBottom: '10px' }
const input = { display: 'block', width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' }
const saveBtn = { display: 'block', width: '100%', padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }
const tabBtn = { padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0 }
const playerRow = { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0' }
const avatar = { width: '36px', height: '36px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '500', color: '#185FA5', flexShrink: 0 }
const addPlayerBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '12px 16px', padding: '12px', borderRadius: '10px', border: '1px dashed #ccc', background: 'none', cursor: 'pointer', fontSize: '14px', color: '#888', width: 'calc(100% - 32px)' }
const th = { padding: '8px 10px', textAlign: 'left', fontSize: '11px', color: '#888', fontWeight: '500', whiteSpace: 'nowrap' }
const td = { padding: '8px 10px', fontSize: '12px', color: '#333' }
const foireannToggleBtn = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#185FA5', fontWeight: '500' }
const guestCheckboxLabel = { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 0', fontSize: '12px', color: '#333', cursor: 'pointer' }